import { useState } from "react";
import { IconClose } from "../icons/Icons";

// ── Stage definitions ─────────────────────────────────────────────────────────

// General overview stages (no specific vehicle)
const GENERAL_STAGES = [
  { id: "HG0", label: "HG0: New Vehicle Entry",                  status: "pending", date: null },
  { id: "HG1", label: "HG1: VSC Approval",                       status: "pending", date: null },
  { id: "HG2", label: "HG2: Vehicle Order Available",            status: "pending", date: null },
  { id: "HG3", label: "HG3: Maturity Check Completed",           status: "pending", date: null },
  { id: "HG4", label: "HG4: Specification Release Granted",      status: "pending", date: null },
  { id: "HG5", label: "HG5: Vehicle in Production",              status: "pending", date: null },
  { id: "HG6", label: "HG6: Vehicle Completed",                  status: "pending", date: null },
];

// Vehicle-specific stages — status derived from vehicle.hgStatus
const HG_ORDER = ["HG0","HG1","HG2","HG3","HG4","HG5","HG6"];

const buildStages = (vehicle) => {
  const currentIdx = HG_ORDER.indexOf(vehicle.hgStatus ?? "HG0");
  return [
    { id: "HG0", label: "HG0: New Vehicle Entry",             status: currentIdx > 0 ? "completed" : currentIdx === 0 ? "active" : "pending", date: null },
    { id: "HG1", label: "HG1: VSC Approval",                  status: currentIdx > 1 ? "completed" : currentIdx === 1 ? "active" : "pending", date: null },
    { id: "HG2", label: `HG2: Order Available${vehicle.desiredDate ? " · " + vehicle.desiredDate : ""}`, status: currentIdx > 2 ? "completed" : currentIdx === 2 ? "active" : "pending", date: vehicle.desiredDate || null },
    { id: "HG3", label: "HG3: Maturity Check Completed",      status: currentIdx > 3 ? "completed" : currentIdx === 3 ? "active" : "pending", date: null },
    { id: "HG4", label: "HG4: Specification Release Granted", status: currentIdx > 4 ? "completed" : currentIdx === 4 ? "active" : "pending", date: null },
    { id: "HG5", label: "HG5: Vehicle in Production",         status: currentIdx > 5 ? "completed" : currentIdx === 5 ? "active" : "pending", date: null },
    { id: "HG6", label: "HG6: Vehicle Completed",             status: currentIdx >= 6 ? "completed" : "pending", date: null },
  ];
};

// ── Per-stage info content ────────────────────────────────────────────────────

const HG_INFO = {
  HG0: {
    title: "HG0: New Vehicle Entry with Desired Production Date on the K-Line",
    points: [
      "Initial creation of the vehicle with a desired production date for the assembly line on the K‑line.",
      "Normally, a dummy vehicle number is used first, because the actual vehicle order has not yet been created. If an order already exists, the correct vehicle number can be entered directly. An existing order is not a guarantee for production on the K‑line.",
      "The vehicle is created by a project manager with basic information about the vehicle (e.g., project assignment, responsible engineers, etc.).",
      "The system calculates a schedule for the different maturity levels based on the desired line‑off date.",
      "Creating a new vehicle in HG0 — and thus requesting production on the K‑line — must be approved by the VSC in the next step.",
    ],
  },
  HG1: {
    title: "HG1: VSC Approval for Building the Vehicle on the K‑Line",
    points: [
      "The vehicle has been approved by the VSC for production on the K‑line.",
      "Usually, there is still no official vehicle order or vehicle number at this stage, so work continues with a dummy vehicle number.",
      "Through the approval for K‑line production, additional vehicle information can be entered. After HG0 is completed, the maturity validation of the vehicle can also be populated.",
      "The line‑off date may change depending on the vehicle's maturity level and other projects.",
      "The target date for HG1 is 82 days, excluding weekends, holidays, and shutdown phases, to reach the line‑off date.",
    ],
  },
  HG2: {
    title: "HG2: Vehicle Order Available (Vehicle No. replaces Dummy No.)",
    points: [
      "An order exists for the dummy vehicle.",
      "The dummy vehicle identification number can now be replaced by a system‑generated vehicle number.",
      "The vehicle's maturity check is progressing (the status of each maturity stage is stored in the tool).",
      "The line‑off date may change depending on the vehicle's maturity state and other projects.",
      "The target timeline for HG2 is 62 days, excluding weekends, holidays, and shutdown phases before reaching the line‑off date.",
    ],
  },
  HG3: {
    title: "HG3: Vehicle Maturity Check Completed (Green Status Light)",
    points: [
      "All vehicle and project information is fully available.",
      "All validation/verification information has been completely documented. All relevant validations have been performed (the information/checklist can be taken from the tool and must be marked as \u201ccompleted\u201d by the responsible person).",
      "Completing the maturity check does not automatically mean that the VSC releases the specification. The release for specification is granted by the VSC during the vehicle planning meeting after the vehicle has been reviewed.",
      "The line‑off date may change depending on the vehicle's maturity status and other projects.",
      "The target timeline for HG3 is 42 days, excluding weekends, holidays, and shutdown phases before the line‑off date.",
    ],
  },
  HG4: {
    title: "HG4: Specification Release (Fassung) Granted for the Vehicle",
    points: [
      "All vehicle and project information is completely available.",
      "The maturity validation for the vehicle has been completed/confirmed in the tool.",
      "The maturity check has been reviewed and validated by the VSC at the latest during the vehicle planning meeting.",
      "The release for specification (\u201cFassung\u201d) is granted during the vehicle planning meeting.",
      "The line‑off date may still be slightly adjusted depending on other projects.",
      "The target timeline for HG4 is 32 days, excluding weekends, holidays, and shutdown phases before the line‑off date.",
      "The specification release in the VSC has no automated effect on the system specification, which must reach 28 days before the line‑off date.",
    ],
  },
  HG5: {
    title: "HG5: Vehicle in Production",
    points: [
      "The vehicle/cab has been released for production in the VSC.",
      "The production progress, including defect progress, is displayed in the VSC Tool.",
    ],
  },
  HG6: {
    title: "HG6: Vehicle Completed",
    points: [
      "The vehicle/cab has been completed in the VSC and delivered to the internal customer.",
    ],
  },
};

// ── Hex Badge ─────────────────────────────────────────────────────────────────

function HexBadge({ id, status, selected }) {
  const clip = "polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)";
  const base = {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 44, height: 44, clipPath: clip,
    fontSize: 10, fontWeight: 700, color: "#fff",
    flexShrink: 0, position: "relative", zIndex: 1,
    letterSpacing: "0.02em", cursor: "pointer",
    outline: status === "active" ? "3px solid #e67e22" : selected ? "2px solid #1a3a4a" : "none",
    outlineOffset: 2,
  };

  const bg =
    status === "completed" ? "#4caf50" :
    status === "active"    ? "#e67e22" :
                             "#1a3a4a";

  return <div style={{ ...base, background: bg }}>{id}</div>;
}

// ── Main modal ────────────────────────────────────────────────────────────────

export default function FahrzeugTrackerModal({ vehicle, onClose }) {
  const isGeneral = !vehicle;
  const stages = isGeneral ? GENERAL_STAGES : buildStages(vehicle);

  // Default selection: active stage for a vehicle, HG0 for general
  const defaultSelected = isGeneral
    ? "HG0"
    : (stages.find(s => s.status === "active")?.id ?? stages[stages.length - 1].id);

  const [selectedHG, setSelectedHG] = useState(defaultSelected);

  const info = HG_INFO[selectedHG];

  const completedCount = stages.filter(s => s.status === "completed").length;
  const activeIdx      = stages.findIndex(s => s.status === "active");
  // Line fills up through the center of the active badge
  const linePercent = isGeneral
    ? 0
    : activeIdx >= 0
      ? Math.round((activeIdx / (stages.length - 1)) * 100)
      : Math.round((completedCount / (stages.length - 1)) * 100);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)" }} />

      {/* Modal */}
      <div style={{
        position: "relative", background: "white", borderRadius: 6,
        width: 900, maxWidth: "96vw", maxHeight: "90vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 12px 48px rgba(0,0,0,0.28)", overflow: "hidden",
        animation: "fadeUp 0.2s ease",
      }}>
        <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }`}</style>

        {/* ── Header ── */}
        <div style={{ background: "#1a3a4a", color: "white", padding: "12px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Vehicle Tracker</div>
            <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>
              {isGeneral
                ? "General HG Stage Overview — click any stage to learn more"
                : `${vehicle.vehicleNumber || vehicle.vehicleName || "–"} · ${vehicle.projectName || ""} ${vehicle.projectNumber || ""}`
              }
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "white", display: "flex", padding: 2 }}>
            <IconClose />
          </button>
        </div>

        {/* ── Body: left timeline + right info ── */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

          {/* LEFT — timeline */}          <div style={{ width: 280, flexShrink: 0, background: "#e8edf0", padding: "24px 20px 24px 24px", borderRight: "1px solid #d0d8de", overflowY: "auto" }}>
            <div style={{ textAlign: "center", fontWeight: 600, fontSize: 12, color: "#333", marginBottom: 22, lineHeight: 1.5 }}>
              {isGeneral ? (
                <span style={{ fontWeight: 700, fontSize: 13 }}>HG Stage Flow</span>
              ) : (
                <>
                  Vehicle:<br />
                  <span style={{ fontWeight: 700, fontSize: 13 }}>
                    {vehicle.vehicleNumber || vehicle.vehicleName || "–"}
                    {vehicle.vehicleName && vehicle.vehicleNumber ? ` (${vehicle.vehicleName})` : ""}
                  </span>
                </>
              )}
            </div>

            {/* Timeline */}
            <div style={{ position: "relative", paddingLeft: 4 }}>
              {/* Vertical connector */}
              <div style={{
                position: "absolute", left: 25, top: 22, bottom: 22, width: 3, zIndex: 0,
                background: `linear-gradient(to bottom, #4caf50 ${linePercent}%, #1a3a4a ${linePercent}%)`,
              }} />              {stages.map((stage, i) => (
                <div
                  key={stage.id}
                  onClick={() => setSelectedHG(stage.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    marginBottom: i < stages.length - 1 ? 16 : 0,
                    cursor: "pointer",
                  }}
                >
                  <HexBadge id={stage.id} status={stage.status} selected={selectedHG === stage.id} />                  <span
                    onMouseEnter={e => { e.currentTarget.style.color = "#1a3a4a"; e.currentTarget.style.fontWeight = "700"; }}
                    onMouseLeave={e => {
                      e.currentTarget.style.color = stage.status === "completed" ? "#388e3c" : stage.status === "active" ? "#c0392b" : "#333";
                      e.currentTarget.style.fontWeight = selectedHG === stage.id ? "700" : stage.status === "active" ? "600" : "400";
                    }}
                    style={{
                      fontSize: 12.5, lineHeight: 1.4,
                      transition: "color 0.15s, font-weight 0.15s",
                      color:
                        stage.status === "completed" ? "#388e3c" :
                        stage.status === "active"    ? "#c0392b" : "#333",
                      fontWeight: selectedHG === stage.id ? 700 : (stage.status === "active" ? 600 : 400),
                      borderBottom: selectedHG === stage.id ? "1.5px solid #1a3a4a" : "1.5px solid transparent",
                    }}
                  >
                    {stage.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — info panel */}
          <div style={{ flex: 1, padding: "28px 30px", overflowY: "auto", background: "white" }}>
            {info ? (
              <>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 18, marginTop: 0, lineHeight: 1.4 }}>
                  {info.title}
                </h2>
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px 0" }}>
                  {info.points.map((point, i) => (
                    <li key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: "#333", marginBottom: 10, lineHeight: 1.6 }}>
                      <span style={{ flexShrink: 0, color: "#1a3a4a", fontWeight: 700, marginTop: 1 }}>–</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
                {info.buttonLabel && (
                  <button
                    style={{
                      background: "#1a3a4a", color: "#fff", border: "none", borderRadius: 4,
                      padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                      letterSpacing: "0.01em", transition: "background 0.18s, transform 0.12s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#0f2530"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "#1a3a4a"; e.currentTarget.style.transform = "translateY(0)"; }}
                    onClick={() => alert("Switch to: Replace Vehicle Number")}
                  >
                    {info.buttonLabel}
                  </button>
                )}
              </>
            ) : (
              <div style={{ color: "#aaa", fontSize: 13 }}>Please select a stage.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
